import React, { useState } from 'react';
import { Badge } from 'antd';
import {
  UnorderedListOutlined,
  EnvironmentOutlined,
  AppstoreOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import type { RootState } from '../../../types/store';
import {
  setCartDrawerOpen,
  setSelectedLocation,
  selectCartTotalElements,
} from '../slice/storefrontSlice';
import { LocationStatusModal } from './LocationStatusModal';
import type { IStoreLocation } from '../types/storefront.types';

interface DockButtonProps {
  /** Nombre accesible (descriptivo). */
  label: string;
  /** Etiqueta corta siempre visible bajo el icono. */
  text: string;
  icon: React.ReactNode;
  /** El activo se destaca con zoom/elevacion (ver .dock-btn[aria-current] en index.css). */
  isActive?: boolean;
  onClick: () => void;
}

/**
 * Item del dock. Es un <button> real: los <div onClick> anteriores no eran
 * enfocables ni anunciables, y los iconos sueltos no tenian nombre accesible.
 */
const DockButton: React.FC<DockButtonProps> = ({
  label,
  text,
  icon,
  isActive = false,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    aria-current={isActive ? 'page' : undefined}
    className="dock-btn"
  >
    {icon}
    <span className="dock-label">{text}</span>
  </button>
);

export const StorefrontFooterDock: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const config = useSelector((state: RootState) => state.storefront.config);
  const selectedLocation = useSelector((state: RootState) => state.storefront.selectedLocation);
  const subdomain = useSelector((state: RootState) => state.storefront.subdomain);
  const isCartOpen = useSelector((state: RootState) => state.storefront.isCartDrawerOpen);

  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);

  const cartItemCount = useSelector(selectCartTotalElements);
  const storeHome = subdomain ? `/store/${subdomain}` : null;
  const isCartaPage = location.pathname === storeHome || location.pathname === `${storeHome}/`;
  const isMenuPage = location.pathname.startsWith(`${storeHome}/menu`);
  const isCheckoutPage = location.pathname.includes('/checkout');

  const handleLocationSelect = (loc: IStoreLocation) => {
    dispatch(setSelectedLocation(loc));
    setIsLocationModalOpen(false);
  };

  if (isCartOpen || isCheckoutPage) return null;

  return (
    <>
      <nav aria-label="Navegacion rapida" className="storefront-dock">
        <DockButton
          label="Menu completo"
          text="Menú"
          icon={<UnorderedListOutlined style={{ fontSize: 20 }} />}
          isActive={isMenuPage}
          onClick={() => storeHome && navigate(`${storeHome}/menu`)}
        />

        <DockButton
          label="Sedes"
          text="Sedes"
          icon={<EnvironmentOutlined style={{ fontSize: 20 }} />}
          onClick={() => setIsLocationModalOpen(true)}
        />

        <DockButton
          label="Carta por categorias"
          text="Carta"
          icon={<AppstoreOutlined style={{ fontSize: 20 }} />}
          isActive={isCartaPage}
          onClick={() => storeHome && navigate(storeHome)}
        />

        <DockButton
          label={`Ver carrito${cartItemCount > 0 ? ` (${cartItemCount})` : ''}`}
          text="Carrito"
          icon={
            <Badge
              count={cartItemCount}
              color="var(--color-badge)"
              offset={[4, -2]}
              style={{
                fontFamily: 'var(--font-mono)',
                fontWeight: 700,
                fontSize: 11,
                color: 'var(--color-badge-contrast)',
              }}
            >
              <ShoppingOutlined style={{ fontSize: 20, color: 'currentColor' }} />
            </Badge>
          }
          onClick={() => dispatch(setCartDrawerOpen(true))}
        />
      </nav>

      <LocationStatusModal
        open={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        locations={config?.partner?.locations || []}
        selectedLocationId={selectedLocation?.id}
        onSelect={handleLocationSelect}
      />
    </>
  );
};
